import { ChangeDetectorRef, inject, Pipe, PipeTransform } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DATE_FNS_LOCALES } from "@core/configs/translation/locales.config";
import { LocaleConfig, TranslationStoreService } from "@ogs-gmbh/ngx-translate";
import { DATE_TYPES, DatePeriod } from "@shared/types/period.type";
import { format, Locale } from "date-fns";
import { isEqual } from "es-toolkit";
import { distinctUntilChanged, filter, Subject, switchMap, tap } from "rxjs";


function formatDatePeriod (datePeriod: DatePeriod, locale: Locale): string {
  const formatString: string = "P";

  return datePeriod.type === DATE_TYPES.timespan
    ? `${ format(datePeriod.startDate, formatString, { locale }) } - ${ format(datePeriod.endDate, formatString, { locale }) }`
    : format(datePeriod.date, formatString, { locale });
}


@Pipe({
  name: "appDatePeriod",
  standalone: true,
  pure: false
})
export class DatePeriodPipe implements PipeTransform {
  private readonly _datePeriod: Subject<DatePeriod | undefined> = new Subject<DatePeriod | undefined>();

  private readonly _translationStoreService: TranslationStoreService = inject(TranslationStoreService);

  private readonly _changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private _latestValue: string = "";

  constructor () {
    this._datePeriod
      .pipe(
        takeUntilDestroyed(),
        filter(Boolean),
        distinctUntilChanged(isEqual),
        switchMap((period: DatePeriod) => this._translationStoreService.getLocale$()
          .pipe(
            tap((localeConfig: LocaleConfig) => {
              const locale: Locale | undefined = DATE_FNS_LOCALES[ localeConfig.value ];

              if (locale)
                this._updateLatestValue(formatDatePeriod(period, locale));
            })
          ))
      )
      .subscribe();
  }


  public transform (value: DatePeriod | undefined): string {
    this._datePeriod.next(value);

    return this._latestValue;
  }


  private _updateLatestValue (transformedValue: string): void {
    if (transformedValue === this._latestValue)
      return;

    this._latestValue = transformedValue;
    this._changeDetectorRef.markForCheck();
  }
}
